import {useCallback, useEffect, useMemo, useState} from "react";
import {ChevronRight, Package, Search, Truck} from "lucide-react";
import {listAdminOrders, type AdminOrderSummary} from "@zaaaz/shared";
import {Input} from "@/components/ui/input";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {AdminBadge, AdminEmptyState, AdminLoading} from "./ui";

type Props = {
  clientId: number;
  onOpenOrder: (orderId: number) => void;
  /** Optional shared orders from parent (avoids double fetch for KPIs). */
  orders?: AdminOrderSummary[];
  loading?: boolean;
  error?: string;
  onOrdersLoaded?: (orders: AdminOrderSummary[]) => void;
};

type SortKey = "newest" | "oldest" | "total-desc" | "total-asc";

/** Premium order cards for a single client — filters are client-side only. */
export function ClientOrdersPanel({
  clientId,
  onOpenOrder,
  orders: ordersProp,
  loading: loadingProp,
  error: errorProp,
  onOrdersLoaded
}: Props) {
  const [items, setItems] = useState<AdminOrderSummary[]>(ordersProp ?? []);
  const [loading, setLoading] = useState(loadingProp ?? !ordersProp);
  const [err, setErr] = useState(errorProp ?? "");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [datePreset, setDatePreset] = useState("all");
  const [sort, setSort] = useState<SortKey>("newest");

  const load = useCallback(() => {
    if(ordersProp) return;
    setLoading(true);
    listAdminOrders({clientId, page: 1, pageSize: 50})
      .then((r) => {
        setItems(r.items);
        setErr("");
        onOrdersLoaded?.(r.items);
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [clientId, onOrdersLoaded, ordersProp]);

  useEffect(() => {
    if(ordersProp) {
      setItems(ordersProp);
      setLoading(Boolean(loadingProp));
      setErr(errorProp ?? "");
      return;
    }
    load();
  }, [load, ordersProp, loadingProp, errorProp]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    const now = Date.now() / 1000;
    let list = items.filter((o) => {
      if(status !== "all" && String(o.status) !== status) return false;
      if(datePreset === "30" && o.date < now - 30 * 86400) return false;
      if(datePreset === "90" && o.date < now - 90 * 86400) return false;
      if(datePreset === "365" && o.date < now - 365 * 86400) return false;
      if(!query) return true;
      const hay = `${o.id} ${o.statusLabel} ${o.payment} ${o.dateLabel} ${o.total} ${o.productCount} ${o.awb || ""}`.toLowerCase();
      return hay.includes(query);
    });
    list = [...list].sort((a, b) => {
      if(sort === "oldest") return a.date - b.date;
      if(sort === "total-desc") return b.total - a.total;
      if(sort === "total-asc") return a.total - b.total;
      return b.date - a.date;
    });
    return list;
  }, [items, q, status, datePreset, sort]);

  if(err) {
    return <p className="text-sm text-destructive">{err}</p>;
  }
  if(loading) {
    return <AdminLoading />;
  }

  return (
    <div>
      <div className="admin-order-filters">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-11 pl-9"
            placeholder="Search order #, status, payment…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-11"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="1">Processing</SelectItem>
            <SelectItem value="5">Paid</SelectItem>
            <SelectItem value="7">Prepared</SelectItem>
            <SelectItem value="2">Shipped</SelectItem>
            <SelectItem value="3">Delivered</SelectItem>
            <SelectItem value="4">Canceled</SelectItem>
            <SelectItem value="6">Payment error</SelectItem>
          </SelectContent>
        </Select>
        <Select value={datePreset} onValueChange={setDatePreset}>
          <SelectTrigger className="h-11"><SelectValue placeholder="Date" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any date</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
          <SelectTrigger className="h-11"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="total-desc">Highest total</SelectItem>
            <SelectItem value="total-asc">Lowest total</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!filtered.length ? (
        <AdminEmptyState
          icon={Package}
          title="No orders"
          description={items.length ? "No orders match these filters." : "This client has not placed any orders yet."}
        />
      ) : (
        <div className="admin-order-list">
          {filtered.map((o) => (
            <button
              key={o.id}
              type="button"
              className="admin-order-row"
              onClick={() => onOpenOrder(o.id)}
            >
              <div className="admin-order-row__top">
                <div>
                  <div className="admin-order-row__id">#{o.id}</div>
                  <div className="admin-order-row__date">{o.dateLabel}</div>
                </div>
                <div className="admin-order-row__badges">
                  <AdminBadge status={o.status}>{o.statusLabel}</AdminBadge>
                  <AdminBadge variant="outline">{o.payment}</AdminBadge>
                  {o.awb ? <AdminBadge variant="outline"><Truck className="h-3 w-3" /> AWB</AdminBadge> : null}
                </div>
              </div>
              <div className="admin-order-row__bottom">
                <div className="admin-order-row__badges">
                  <AdminBadge variant="secondary"><Package className="h-3 w-3" /> {o.productCount} items</AdminBadge>
                </div>
                <div className="inline-flex items-center gap-2">
                  <span className="admin-order-row__total">{o.total.toFixed(2)} Lei</span>
                  <ChevronRight className="admin-order-row__chevron h-4 w-4" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
